import { Component, OnInit, ChangeDetectorRef, ViewChild, AfterViewInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators, FormArray } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { AppConstants } from 'src/app/shared/constants/app.contants';
import { LoaderService } from 'src/app/shared/services/loader.service';
import { ApiService } from 'src/app/shared/services/api.service';
import { ToasterService } from 'src/app/shared/services/toaster.service';
import { UserService } from 'src/app/shared/services/user.service';
import { FileUploadComponent } from 'src/app/shared/components/file-upload/file-upload.component';
import { environment } from 'src/environments/environment';
import { lastValueFrom } from 'rxjs';
import { HttpClient } from '@angular/common/http';

interface AgendaItem {
  id: number | string;
  subject: string;
  agendaTitleAR?: string;
  agendaTitleEN?: string;
  linkPR?: string;
  currentStage?: string;
  prIdentifiers?: string[];
  department?: string;
  tcDecisionAR?: string;
  tcDecisionEN?: string;
}

interface User {
  id: number;
  fullName: string;
  email: string;
  role: string; // Role name as string from backend (e.g., "TCM", "ViceChairman", "Chairman", "Secretary", "Observer")
  isHoster?: boolean; // True for the secretary who is creating the meeting
}

interface PurchaseRequest {
  prNumber: string;
  department: string;
  value: number;
  description: string;
  status: string;
}

@Component({
  selector: 'app-create-meeting',
  templateUrl: './create-meeting.component.html',
  styleUrls: ['./create-meeting.component.scss']
})
export class CreateMeetingComponent implements OnInit, AfterViewInit {
  @ViewChild('fileUploadComponent') fileUploadComponent!: FileUploadComponent;
  AppConstants = AppConstants;
  meetingForm: FormGroup;
  
  // Members data
  allMembers: User[] = [];
  /** Members list used for display (non-observers, with only the creating Secretary as hoster) */
  displayMembers: User[] = [];
  selectedMembers: User[] = [];
  chairmanName: string = '';
  
  // Observers data
  allObservers: User[] = [];
  filteredObservers: User[] = [];
  selectedObservers: User[] = [];
  observerSearchTerm: string = '';
  
  // Existing agenda items
  existingAgendaItems: AgendaItem[] = [];
  filteredAgendaItems: AgendaItem[] = [];
  selectedAgendaItems: AgendaItem[] = [];
  agendaSearchTerm: string = '';
  currentStages: string[] = ['Draft', 'Under Review', 'Pending Approval', 'Approved', 'Rejected'];
  locationOptions: string[] = ['On site', 'Hybrid', 'Online'];
  purchaseRequests: PurchaseRequest[] = [];
  isLoading: boolean = false;
  isEmergencyMeeting: boolean = false;
  isEditMode: boolean = false;
  meetingIdToEdit: string | null = null;
  meetingIdInt: number | null = null;

  constructor(
    private fb: FormBuilder,
    private _router: Router,
    private _route: ActivatedRoute,
    private _loaderService: LoaderService,
    private apiService: ApiService,
    private cdr: ChangeDetectorRef,
    private toasterService: ToasterService,
    private userService: UserService,
    private http: HttpClient
  ) {
    this.meetingForm = this.fb.group({
      meetingTitle: ['', Validators.required],
      meetingId: [{ value: '', disabled: true }], // No validator needed since it's auto-generated
      dateTime: ['', Validators.required],
      location: ['', Validators.required],
      membersInvited: [[]],
      observersInvited: [[]],
      selectedAgendaItems: [[]],
      agendaNotes: [''],
      agendaItems: this.fb.array([]),
      attachments: [null]
    });
    
    // Initialize with empty selections
    this.selectedMembers = [];
    this.chairmanName = '';
    this.selectedObservers = [];
    this.selectedAgendaItems = [];
    
    // Watch for dateTime changes to calculate emergency meeting status
    this.meetingForm.get('dateTime')?.valueChanges.subscribe((dateTime: string) => {
      this.calculateEmergencyMeetingStatus(dateTime);
    });
  }

  async ngOnInit(): Promise<void> {
    // Check if we're in edit mode
    const params = this._route.snapshot.queryParams;
    if (params['edit'] === 'true' && params['id']) {
      this.isEditMode = true;
      this.meetingIdToEdit = params['id'];
    }
    
    // Load data from APIs first (needed for edit mode)
    await this.loadUsers();
    await this.loadAgendaItems();
    await this.loadPurchaseRequests();
    
    // Load meeting data if in edit mode
    if (this.isEditMode && this.meetingIdToEdit) {
      await this.loadMeetingForEdit();
    } else {
      // Only generate meeting ID if not in edit mode
      await this.generateMeetingId();
    }
  }

  ngAfterViewInit(): void {
    // Load attachments for edit mode after view is initialized
    if (this.isEditMode && this.meetingIdInt && this.fileUploadComponent) {
      setTimeout(() => {
        this.loadMeetingAttachments();
      }, 100);
    }
  }

  async generateMeetingId(): Promise<void> {
    try {
      // Fetch existing meetings to determine next ID
      const url = `${environment.API_URL}/api/Meetings`;
      const response: any = await lastValueFrom(this.apiService.getByUrl(url));
      
      let nextId = 1;
      if (response?.isSuccess && response?.data && response.data.length > 0) {
        // Extract numeric part from existing meeting IDs and find the max
        const meetingIds = response.data
          .map((m: any) => {
            const match = m.meetingId?.match(/M-(\d+)/i);
            return match ? parseInt(match[1], 10) : 0;
          })
          .filter((id: number) => id > 0);
        
        if (meetingIds.length > 0) {
          nextId = Math.max(...meetingIds) + 1;
        }
      }
      
      // Format as M-XXX (e.g., M-001, M-002, etc.)
      const meetingId = `M-${nextId.toString().padStart(3, '0')}`;
      this.meetingForm.patchValue({ meetingId: meetingId });
    } catch (error) {
      console.error('Error generating meeting ID:', error);
      // Fallback: use timestamp-based ID
      const fallbackId = `M-${Date.now().toString().slice(-6)}`;
      this.meetingForm.patchValue({ meetingId: fallbackId });
    }
  }

  async loadUsers(): Promise<void> {
    try {
      this.isLoading = true;
      const url = `${environment.API_URL}/api/Meetings/users`;
      const response: any = await lastValueFrom(this.apiService.getByUrl(url));
      
      if (response?.isSuccess && response?.data) {
        // Backend returns role as string name (e.g., "TCM", "ViceChairman", "Chairman", "Secretary", "Observer")
        this.allMembers = response.data.map((user: any) => ({
          id: user.id || user.Id,
          fullName: user.fullName || user.FullName,
          email: user.email || user.Email,
          role: user.role || user.Role || 'TCM' // Role name as string from backend
        }));
        
        // Determine current logged-in user (Secretary creating the meeting)
        const currentUserId = this.userService.user?.Id?.toString() || '';
        const currentUserEmail = (this.userService.user?.EmailAddress || '').toString().toLowerCase();
        const isCurrentUser = (member: User): boolean => {
          const memberId = member.id?.toString() || '';
          const memberEmail = (member.email || '').toString().toLowerCase();
          return (!!currentUserId && memberId === currentUserId) ||
                 (!!currentUserEmail && memberEmail === currentUserEmail);
        };

        // Rule: When a meeting is created, all members are initially selected and disabled
        // Only members with the roles of TCM, vice chairman, or chairman can be manually selected
        // Filter observers from all members (observers should be separate)
        this.allObservers = this.allMembers.filter(member => {
          return this.isObserverRole(member.role);
        });
        this.filteredObservers = [...this.allObservers];
        this.selectedObservers = [];
        this.meetingForm.patchValue({ observersInvited: [] });
        
        // Filter out observers from members (members should not include observers)
        let membersWithoutObservers = this.allMembers.filter(member => {
          return !this.isObserverRole(member.role);
        });

        // Exclude all Secretaries except the current logged-in Secretary (hoster)
        membersWithoutObservers = membersWithoutObservers.filter(member => {
          const roleLower = (member.role || '').toString().toLowerCase();
          const isSecretaryRole = roleLower === 'secretary' || roleLower === 'tender committee secretary';
          if (!isSecretaryRole) {
            return true;
          }
          // Keep only the current user as Secretary (hoster)
          if (isCurrentUser(member)) {
            member.isHoster = true;
            return true;
          }
          // Exclude other secretaries from the list
          return false;
        });

        // Store display list (non-observers, with only creating Secretary as hoster)
        this.displayMembers = [...membersWithoutObservers];

        // Auto-select all display members returned from backend
        this.selectedMembers = [...this.displayMembers];
        this.meetingForm.patchValue({ membersInvited: this.selectedMembers.map(m => m.id) });
        
        this.cdr.markForCheck();
      } else {
        console.error('Failed to load users:', response?.errorMessageEn);
        this.allMembers = [];
        this.selectedMembers = [];
        this.allObservers = [];
        this.filteredObservers = [];
        this.selectedObservers = [];
        this.cdr.markForCheck();
      }
    } catch (error) {
      console.error('Error loading users:', error);
      this.allMembers = [];
      this.selectedMembers = [];
      this.allObservers = [];
      this.filteredObservers = [];
      this.selectedObservers = [];
      this.cdr.markForCheck();
    } finally {
      this.isLoading = false;
      this.cdr.markForCheck();
    }
  }

  async loadAgendaItems(): Promise<void> {
    try {
      this.isLoading = true;
      // Exclude agenda items that are already linked to a meeting (only when creating new meeting, not editing)
      // When editing, we want to show all agenda items including those linked to the current meeting
      const excludeLinked = !this.isEditMode;
      const url = `${environment.API_URL}/api/AgendaItems?excludeLinked=${excludeLinked}`;
      const response: any = await lastValueFrom(this.apiService.getByUrl(url));
      
      if (response?.isSuccess && response?.data) {
        // Create new array references to ensure change detection
        this.existingAgendaItems = response.data.map((item: any) => ({
          id: item.id,
          subject: item.subject,
          agendaTitleAR: item.agendaTitleAR,
          agendaTitleEN: item.agendaTitleEN,
          currentStage: item.currentStage,
          prIdentifiers: item.prIdentifiers || []
        }));
        this.filteredAgendaItems = [...this.existingAgendaItems];
        this.cdr.markForCheck();
      } else {
        console.error('Failed to load agenda items:', response?.errorMessageEn);
        this.existingAgendaItems = [];
        this.filteredAgendaItems = [];
        this.cdr.markForCheck();
      }
    } catch (error) {
      console.error('Error loading agenda items:', error);
      this.existingAgendaItems = [];
      this.filteredAgendaItems = [];
      this.cdr.markForCheck();
    } finally {
      this.isLoading = false;
      this.cdr.markForCheck();
    }
  }

  async loadPurchaseRequests(): Promise<void> {
    try {
      const url = `${environment.API_URL}/api/PurchaseRequests`;
      const response: any = await lastValueFrom(this.apiService.getByUrl(url));
      
      if (response?.isSuccess && response?.data) {
        // Create new array reference to ensure change detection
        this.purchaseRequests = response.data.map((pr: any) => ({
          prNumber: pr.prNumber,
          department: pr.department,
          value: pr.value,
          description: pr.description,
          status: pr.status
        }));
      } else {
        console.error('Failed to load purchase requests:', response?.errorMessageEn);
        this.purchaseRequests = [];
      }
    } catch (error) {
      console.error('Error loading purchase requests:', error);
      this.purchaseRequests = [];
    }
  }

  async loadMeetingForEdit(): Promise<void> {
    if (!this.meetingIdToEdit) {
      console.error('loadMeetingForEdit: meetingIdToEdit is missing');
      return;
    }

    try {
      this._loaderService.showLoader();
      const url = `${environment.API_URL}/api/Meetings/${this.meetingIdToEdit}`;
      console.log('Loading meeting for edit, URL:', url, 'MeetingId:', this.meetingIdToEdit);
      const response: any = await lastValueFrom(this.apiService.getByUrl(url));
      
      console.log('Meeting edit response:', response);
      
      if (response?.isSuccess && response?.data) {
        const meeting = response.data;
        
        // Check if meeting status is Draft - only allow editing Draft meetings
        const status = meeting.status || meeting.statusName || '';
        const statusLower = status.toString().toLowerCase();
        const isDraft = statusLower === 'draft' || statusLower === '1' || status === 1 || status === 'Draft';
        
        if (!isDraft) {
          this.toasterService.showError('Only meetings with Draft status can be edited.');
          this._router.navigate([`/${AppConstants.ROUTES.MEETINGS}`]);
          return;
        }
        
        this.meetingIdInt = meeting.id;

        // Format dateTime for datetime-local input (YYYY-MM-DDTHH:mm)
        // Backend returns UTC ISO string like "2026-01-27T00:30:00.000Z"
        // Simply strip the seconds and Z suffix to get "2026-01-27T00:30"
        let formattedDateTime = meeting.dateTime;
        if (formattedDateTime) {
          // Remove seconds, milliseconds, and Z suffix: "2026-01-27T00:30:00.000Z" -> "2026-01-27T00:30"
          formattedDateTime = formattedDateTime.replace(/:\d{2}\.\d{3}Z$/, '').replace(/:\d{2}Z$/, '');
          // If format still has seconds but no Z, remove them
          if (formattedDateTime.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/)) {
            formattedDateTime = formattedDateTime.substring(0, 16); // Keep only YYYY-MM-DDTHH:mm
          }
        }

        // Populate form fields
        this.meetingForm.patchValue({
          meetingTitle: meeting.meetingTitle || '',
          meetingId: meeting.meetingId || '',
          dateTime: formattedDateTime,
          location: meeting.location || '',
          agendaNotes: meeting.agendaNotes || ''
        });

        // Set chairman name if available
        if (meeting.chairmanName) {
          this.chairmanName = meeting.chairmanName;
        }

        // Populate selected members (users should already be loaded)
        // Exclude observers from members - they should be in observers list
        if (meeting.members && Array.isArray(meeting.members)) {
          const nonObserverMembers = meeting.members.filter((member: any) => {
            return !this.isObserverRole(member.role);
          });
          
          this.selectedMembers = nonObserverMembers
            .map((member: any) => {
              // Find matching user from allMembers by userId or email
              return this.allMembers.find(u => 
                u.id === member.userId || 
                u.email === member.userEmail
              );
            })
            .filter((user: User | undefined): user is User => user !== undefined);
          
          this.meetingForm.patchValue({ 
            membersInvited: this.selectedMembers.map(m => m.id) 
          });
        }

        // Populate selected observers (filter observers from members)
        if (meeting.members && Array.isArray(meeting.members)) {
          const observerMembers = meeting.members.filter((member: any) => {
            return this.isObserverRole(member.role);
          });
          
          this.selectedObservers = observerMembers
            .map((member: any) => {
              // Find matching user from allObservers by userId or email
              return this.allObservers.find(u => 
                u.id === member.userId || 
                u.email === member.userEmail
              );
            })
            .filter((user: User | undefined): user is User => user !== undefined);
          
          this.meetingForm.patchValue({ 
            observersInvited: this.selectedObservers.map(o => o.id) 
          });
        }

        // Populate selected agenda items (agenda items should already be loaded)
        if (meeting.agendaItems && Array.isArray(meeting.agendaItems)) {
          this.selectedAgendaItems = meeting.agendaItems
            .map((agendaItem: any) => {
              // Find matching agenda item from existingAgendaItems
              return this.existingAgendaItems.find(ai => ai.id === agendaItem.id);
            })
            .filter((item: AgendaItem | undefined): item is AgendaItem => item !== undefined);
          
          this.meetingForm.patchValue({ 
            selectedAgendaItems: this.selectedAgendaItems.map(item => item.id) 
          });
        }

        // Calculate emergency meeting status
        this.calculateEmergencyMeetingStatus(formattedDateTime);
      } else {
        console.error('Failed to load meeting:', response?.errorMessageEn);
        this.toasterService.showError('Failed to load meeting data. Please try again.');
        this._router.navigate([`/${AppConstants.ROUTES.MEETINGS}`]);
      }
    } catch (error: any) {
      console.error('Error loading meeting for edit:', error);
      console.error('Error details:', {
        message: error?.message,
        error: error?.error,
        status: error?.status,
        statusText: error?.statusText,
        meetingIdToEdit: this.meetingIdToEdit
      });
      
      const errorMessage = error?.error?.errorMessageEn || 
                          error?.error?.message || 
                          error?.message || 
                          'An error occurred while loading the meeting. Please try again.';
      
      this.toasterService.showError(errorMessage);
      this._router.navigate([`/${AppConstants.ROUTES.MEETINGS}`]);
    } finally {
      this._loaderService.hideLoader();
    }
  }

  get agendaItemsFormArray(): FormArray {
    return this.meetingForm.get('agendaItems') as FormArray;
  }

  addAgendaItem(): void {
    const agendaItemForm = this.fb.group({
      subject: ['', Validators.required],
      linkPR: [''],
      currentStage: ['']
    });
    this.agendaItemsFormArray.push(agendaItemForm);
  }

  removeAgendaItem(index: number): void {
    if (this.agendaItemsFormArray.length > 1) {
      this.agendaItemsFormArray.removeAt(index);
    }
  }

  // Helper function to check if role is in allowed roles list
  private isRoleAllowed(role: string, allowedRoles: string[]): boolean {
    return allowedRoles.includes(role);
  }
  
  // Helper function to format role name for display (e.g., "ViceChairman" -> "Vice Chairman")
  private formatRoleName(role: string): string {
    // Add space before capital letters (except the first one)
    return role.replace(/([a-z])([A-Z])/g, '$1 $2');
  }

  // Members multi-select methods
  toggleMemberSelection(member: User): void {
    // Rule: Only members with roles TCM, ViceChairman, Chairman, or Secretary can be manually selected
    // Secretary = Chairman, so Secretary can be selected
    // All members are initially selected and disabled, but can be toggled if they have the right role
    const allowedRoles = ['TCM', 'ViceChairman', 'Chairman', 'Secretary'];
    
    if (!this.isRoleAllowed(member.role, allowedRoles)) {
      // Don't allow selection/deselection of members without allowed roles
      return;
    }

    const index = this.selectedMembers.findIndex(m => m.id === member.id);
    if (index > -1) {
      this.selectedMembers.splice(index, 1);
      // If removing chairman, clear chairman
      if (this.chairmanName === member.fullName) {
        this.chairmanName = '';
      }
    } else {
      this.selectedMembers.push(member);
      // If first member, set as chairman
      if (this.selectedMembers.length === 1) {
        this.chairmanName = member.fullName;
      }
    }
    this.meetingForm.patchValue({ membersInvited: this.selectedMembers.map(m => m.id) });
  }

  isMemberSelected(member: User): boolean {
    return this.selectedMembers.some(m => m.id === member.id);
  }

  isMemberDisabled(member: User): boolean {
    // Rule: All members are initially selected and disabled
    // Only members with roles TCM, ViceChairman, Chairman, or Secretary can be manually selected
    // Secretary = Chairman, so Secretary can be selected
    const allowedRoles = ['TCM', 'ViceChairman', 'Chairman', 'Secretary'];
    return !this.isRoleAllowed(member.role, allowedRoles);
  }

  getMemberRole(member: User): string {
    // Backend returns role as string name (e.g., "TCM", "ViceChairman", "Chairman", "Secretary", "Observer")
    // Format it nicely for display (e.g., "ViceChairman" -> "Vice Chairman")
    return this.formatRoleName(member.role || 'Unknown');
  }

  // Get members excluding observers
  getNonObserverMembers(): User[] {
    // Use the prepared displayMembers list (non-observers, with only creating Secretary as hoster)
    return this.displayMembers;
  }

  // Check if a role is Observer (case-insensitive, handles spaces and variations)
  isObserverRole(role: string | undefined | null): boolean {
    if (!role) return false;
    
    // Convert to string and normalize
    const roleStr = String(role).trim();
    const roleLower = roleStr.toLowerCase().replace(/\s+/g, '');
    
    // Handle variations: "observer", "observers", "Observer", "Observers", etc.
    return roleLower === 'observer' || roleLower === 'observers';
  }

  // Normalize role string (remove spaces, lowercase)
  normalizeRole(role: string | undefined | null): string {
    if (!role) return '';
    return role.toLowerCase().replace(/\s+/g, '');
  }

  // Agenda items multi-select methods
  toggleAgendaItemSelection(item: AgendaItem): void {
    const index = this.selectedAgendaItems.findIndex(agenda => agenda.id === item.id);
    if (index > -1) {
      this.selectedAgendaItems.splice(index, 1);
    } else {
      this.selectedAgendaItems.push(item);
    }
    this.meetingForm.patchValue({ selectedAgendaItems: this.selectedAgendaItems.map(ai => ai.id) });
  }

  isAgendaItemSelected(item: AgendaItem): boolean {
    return this.selectedAgendaItems.some(agenda => agenda.id === item.id);
  }

  // Drag and drop methods for reordering agenda items
  private draggedIndex: number | null = null;

  onDragStart(index: number): void {
    this.draggedIndex = index;
  }

  onDragOver(event: DragEvent, index: number): void {
    event.preventDefault();
    event.stopPropagation();
  }

  onDrop(event: DragEvent, dropIndex: number): void {
    event.preventDefault();
    event.stopPropagation();

    if (this.draggedIndex !== null && this.draggedIndex !== dropIndex) {
      // Reorder the array
      const draggedItem = this.selectedAgendaItems[this.draggedIndex];
      this.selectedAgendaItems.splice(this.draggedIndex, 1);
      this.selectedAgendaItems.splice(dropIndex, 0, draggedItem);

      // Update form value
      this.meetingForm.patchValue({ 
        selectedAgendaItems: this.selectedAgendaItems.map(ai => ai.id) 
      });

      console.log('Agenda items reordered:', this.selectedAgendaItems);
    }

    this.draggedIndex = null;
  }

  onDragEnd(): void {
    this.draggedIndex = null;
  }

  // Filter agenda items based on search term
  filterAgendaItems(): void {
    if (!this.agendaSearchTerm || this.agendaSearchTerm.trim() === '') {
      this.filteredAgendaItems = [...this.existingAgendaItems];
      return;
    }

    const searchTerm = this.agendaSearchTerm.toLowerCase().trim();
    this.filteredAgendaItems = this.existingAgendaItems.filter(item => {
      // Search in subject
      const subjectMatch = item.subject?.toLowerCase().includes(searchTerm);
      // Search in PR identifiers
      const prMatch = item.prIdentifiers?.some(pr => pr.toLowerCase().includes(searchTerm));
      // Search in currentStage
      const stageMatch = item.currentStage?.toLowerCase().includes(searchTerm);
      
      return subjectMatch || prMatch || stageMatch;
    });
  }

  onAgendaSearchChange(): void {
    this.filterAgendaItems();
  }

  getAgendaStageClass(stage: any): string {
    const value = (stage || '').toString().toLowerCase().replace(/[^a-z0-9]/g, '');
    if (value.includes('unsealingtechnical')) return 'stage-unseal-technical';
    if (value.includes('unsealingcommercial')) return 'stage-unseal-commercial';
    if (value.includes('unsealingcombined')) return 'stage-unseal-combined';
    if (value.includes('evaluationreview')) return 'stage-evaluation-review';
    if (value.includes('awardbid')) return 'stage-award-bid';
    return '';
  }

  // Observers multi-select methods
  toggleObserverSelection(observer: User): void {
    const index = this.selectedObservers.findIndex(o => o.id === observer.id);
    if (index > -1) {
      this.selectedObservers.splice(index, 1);
    } else {
      this.selectedObservers.push(observer);
    }
    this.meetingForm.patchValue({ observersInvited: this.selectedObservers.map(o => o.id) });
  }

  isObserverSelected(observer: User): boolean {
    return this.selectedObservers.some(o => o.id === observer.id);
  }

  // Filter observers based on search term
  filterObservers(): void {
    if (!this.allObservers || this.allObservers.length === 0) {
      this.filteredObservers = [];
      return;
    }

    if (!this.observerSearchTerm || this.observerSearchTerm.trim() === '') {
      this.filteredObservers = [...this.allObservers];
      return;
    }

    const searchTerm = this.observerSearchTerm.toLowerCase().trim();
    this.filteredObservers = this.allObservers.filter(observer => {
      // Search in full name
      const nameMatch = observer.fullName?.toLowerCase().includes(searchTerm);
      // Search in email
      const emailMatch = observer.email?.toLowerCase().includes(searchTerm);
      // Search in role
      const roleMatch = observer.role?.toLowerCase().includes(searchTerm);
      
      return nameMatch || emailMatch || roleMatch;
    });
  }

  onObserverSearchChange(): void {
    this.filterObservers();
  }

  async loadMeetingAttachments(): Promise<void> {
    if (!this.meetingIdInt || !this.fileUploadComponent) {
      return;
    }

    try {
      // Fetch documents for this meeting
      const documentsUrl = `${environment.API_URL}/api/Documents/GetDocuments`;
      const documentsDto = {
        applicationId: this.meetingIdInt.toString(),
        processName: 'Meeting',
        processID: this.meetingIdInt.toString()
      };

      const documentsResponse: any = await lastValueFrom(this.apiService.postByUrl(documentsUrl, documentsDto));

      if (documentsResponse?.isSuccess && documentsResponse?.data) {
        if (Array.isArray(documentsResponse.data) && documentsResponse.data.length > 0) {
          const documents = documentsResponse.data;
          
          // Use direct blob access (SAS URLs) - same as in create-agenda-item-dialog
          const USE_DIRECT_BLOB_ACCESS = true;
          
          const filePromises = documents
            .filter((doc: any) => {
              if (USE_DIRECT_BLOB_ACCESS) {
                return doc.fileContent && doc.name;
              } else {
                return doc.attachmentId && doc.name;
              }
            })
            .map(async (doc: any) => {
              try {
                let blob: Blob;
                let contentType: string;
                
                if (USE_DIRECT_BLOB_ACCESS) {
                  // DIRECT ACCESS: Fetch directly from Azure Blob Storage (requires CORS)
                  const fileUrl = doc.fileContent; // This is the SAS URL from backend
                  
                  const response = await fetch(fileUrl, {
                    method: 'GET',
                    headers: {
                      'Origin': window.location.origin
                    }
                  });
                  
                  if (!response.ok) {
                    console.error(`Failed to fetch file from URL: ${fileUrl}`, response.statusText);
                    return null;
                  }
                  
                  blob = await response.blob();
                  contentType = doc.mimeType || blob.type || 'application/octet-stream';
                } else {
                  // BACKEND PROXY: Use backend endpoint (fallback if CORS not configured)
                  const downloadUrl = `${environment.API_URL}/api/Documents/DownloadDocument/${doc.attachmentId}`;
                  
                  const response = await this.http.get(downloadUrl, { 
                    responseType: 'blob',
                    observe: 'response'
                  }).toPromise();
                  
                  if (!response || !response.body) {
                    console.error(`Failed to fetch file for attachment ${doc.attachmentId}`);
                    return null;
                  }
                  
                  blob = response.body;
                  contentType = response.headers.get('content-type') || doc.mimeType || 'application/octet-stream';
                }
                
                // Create a File object from the blob with proper name and type
                const file = new File([blob], doc.name, { 
                  type: contentType,
                  lastModified: Date.now()
                });

                return file;
              } catch (error) {
                console.error(`Error loading file ${doc.name}:`, error);
                return null;
              }
            });

          const files = await Promise.all(filePromises);
          const validFiles = files.filter((file): file is File => file !== null);
          
          // Push files to the file-upload component
          validFiles.forEach(file => {
            this.fileUploadComponent.pushFileToUploadedFiles(file.name, file, file.type);
          });
        }
      }
    } catch (error) {
      console.error('Error loading meeting attachments:', error);
    }
  }

  calculateEmergencyMeetingStatus(dateTime: string): void {
    if (!dateTime) {
      this.isEmergencyMeeting = false;
      return;
    }

    const meetingDate = new Date(dateTime);
    const now = new Date();
    const timeUntilMeeting = meetingDate.getTime() - now.getTime();
    const daysUntilMeeting = timeUntilMeeting / (1000 * 60 * 60 * 24);

    // Rule: If published less than 2 days before scheduled time, it's an emergency meeting
    // We show a preview: if the meeting is scheduled less than 2 days from now, it will be an emergency meeting when published
    this.isEmergencyMeeting = daysUntilMeeting < 2 && daysUntilMeeting >= 0;
  }

  getEmergencyMeetingStatus(): string {
    return this.isEmergencyMeeting ? 'True' : 'False';
  }

  goBack(): void {
    this._router.navigate([`/${AppConstants.ROUTES.MEETINGS}`]);
  }

  async onCreate(): Promise<void> {
    console.log('onCreate called');
    
    // Check form validity - need to check manually since disabled fields don't participate in validation
    const formValue = this.meetingForm.getRawValue();
    console.log('Form values:', formValue);
    
    // Rule: One must be chairman, vice chairman, or secretary
    // Secretary = Chairman, so Secretary satisfies the requirement
    const hasChairmanOrViceChairman = this.selectedMembers.some(m => {
      return m.role === 'ViceChairman' || m.role === 'Chairman' || m.role === 'Secretary';
    });

    const isFormValid = this.meetingForm.get('meetingTitle')?.valid &&
                       this.meetingForm.get('dateTime')?.valid &&
                       this.meetingForm.get('location')?.valid &&
                       formValue.meetingId && // Check meetingId exists (it's disabled but should have value)
                       hasChairmanOrViceChairman && // At least one chairman or vice chairman
                       this.selectedAgendaItems.length > 0; // At least one agenda item must be selected

    console.log('Form valid:', isFormValid);
    console.log('Meeting title valid:', this.meetingForm.get('meetingTitle')?.valid);
    console.log('DateTime valid:', this.meetingForm.get('dateTime')?.valid);
    console.log('Location valid:', this.meetingForm.get('location')?.valid);
    console.log('MeetingId:', formValue.meetingId);

    if (isFormValid) {
      this._loaderService.showLoader();
      try {
        // Prepare agenda items from form array (newly created ones)
        const newAgendaItems = this.agendaItemsFormArray.value.map((item: any) => ({
          agendaTitleAR: item.subject || '',
          agendaTitleEN: item.subject || '',
          subject: item.subject || '',
          currentStage: item.currentStage || null,
          prIdentifiers: item.linkPR ? [item.linkPR] : []
        }));

        // Create new agenda items first if any
        // Note: MeetingId will be set after meeting is created, so we'll link them via MeetingAgendaItems table
        const createdAgendaItemIds: number[] = [];
        for (const agendaItem of newAgendaItems) {
          if (agendaItem.subject) {
            try {
              const url = `${environment.API_URL}/api/AgendaItems`;
              // Don't pass meetingId here - it will be linked via MeetingAgendaItems table after meeting creation
              const response: any = await lastValueFrom(this.apiService.postByUrl(url, agendaItem));
              if (response?.isSuccess && response?.data?.id) {
                createdAgendaItemIds.push(response.data.id);
              }
            } catch (error) {
              console.error('Error creating agenda item:', error);
            }
          }
        }

        // Combine selected existing agenda items with newly created ones
        const allAgendaItemIds = [
          ...this.selectedAgendaItems.map(item => typeof item.id === 'number' ? item.id : parseInt(item.id.toString())),
          ...createdAgendaItemIds
        ];

        // Format dateTime for backend (convert to ISO string WITHOUT timezone conversion)
        let dateTimeValue = formValue.dateTime;
        if (dateTimeValue) {
          // datetime-local format is "YYYY-MM-DDTHH:mm" (no timezone)
          // Treat the selected date/time AS-IS without timezone conversion
          // Simply append ":00.000Z" to make it a valid UTC ISO string
          if (dateTimeValue.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/)) {
            // Convert "2026-01-27T00:30" to "2026-01-27T00:30:00.000Z"
            dateTimeValue = dateTimeValue + ':00.000Z';
          } else {
            // Fallback: if format is unexpected, just ensure it's a valid ISO string
            dateTimeValue = new Date(dateTimeValue + 'Z').toISOString();
          }
        }

        // Prepare meeting data
        // formValue already contains disabled field value from getRawValue()
        const meetingData = {
          meetingTitle: formValue.meetingTitle,
          meetingId: formValue.meetingId,
          dateTime: dateTimeValue,
          location: formValue.location,
          chairmanName: this.chairmanName || null,
          agendaNotes: formValue.agendaNotes || null,
          // Status is not sent - backend will default to Draft
          memberIds: this.selectedMembers.map(m => m.id),
          observerIds: this.selectedObservers.map(o => o.id),
          agendaItemIds: allAgendaItemIds
        };

        // Create or update meeting
        let response: any;
        
        // Get uploaded files from file-upload component
        const uploadedFiles: File[] = this.fileUploadComponent?.getUploadedFiles() || [];
        const hasFiles = uploadedFiles.length > 0;
        
        const url = this.isEditMode && this.meetingIdInt
          ? `${environment.API_URL}/api/Meetings/${this.meetingIdInt}`
          : `${environment.API_URL}/api/Meetings`;

        if (hasFiles) {
          // If files are present, use FormData (multipart/form-data) via ApiService
          // Add processName to payload for file attachment type
          const payloadWithProcessName = {
            ...meetingData,
            processName: 'Meeting'
          };
          
          if (this.isEditMode && this.meetingIdInt) {
            response = await lastValueFrom(this.apiService.putMultipartByUrl(url, payloadWithProcessName, uploadedFiles));
          } else {
            response = await lastValueFrom(this.apiService.postMultipartByUrl(url, payloadWithProcessName, uploadedFiles));
          }
        } else {
          // No files, use regular JSON request
          if (this.isEditMode && this.meetingIdInt) {
            // Update existing meeting
            response = await lastValueFrom(this.apiService.putByUrl(url, meetingData));
          } else {
            // Create new meeting
            response = await lastValueFrom(this.apiService.postByUrl(url, meetingData));
          }
        }

        if (response?.isSuccess) {
          // Success - show success message and navigate to meetings list
          const message = this.isEditMode ? 'Meeting updated successfully!' : 'Meeting created successfully!';
          this.toasterService.showSuccess(message);
          this._router.navigate([`/${AppConstants.ROUTES.MEETINGS}`]);
        } else {
          // Show error message
          const action = this.isEditMode ? 'update' : 'create';
          console.error(`Failed to ${action} meeting:`, response?.errorMessageEn);
          this.toasterService.showError(response?.errorMessageEn || `Failed to ${action} meeting. Please try again.`);
        }
      } catch (error: any) {
        console.error('Error creating meeting:', error);
        this.toasterService.showError(error?.error?.errorMessageEn || 'An error occurred while creating the meeting. Please try again.');
      } finally {
        this._loaderService.hideLoader();
      }
    } else {
      console.log('Form validation failed');
      // Mark form as touched to show validation errors
      this.meetingForm.markAllAsTouched();
      // Mark all fields as touched to show validation errors
      Object.keys(this.meetingForm.controls).forEach(key => {
        const control = this.meetingForm.get(key);
        if (control && !control.disabled) {
          control.markAsTouched();
        }
      });
      
      // Show user-friendly error messages using toaster
      if (!this.meetingForm.get('meetingTitle')?.valid) {
        this.toasterService.showError('Please enter a meeting title');
      } else if (!this.meetingForm.get('dateTime')?.valid) {
        this.toasterService.showError('Please select a date and time');
      } else if (!this.meetingForm.get('location')?.valid) {
        this.toasterService.showError('Please select a location');
      } else if (!formValue.meetingId) {
        this.toasterService.showError('Meeting ID is missing. Please refresh the page.');
      } else if (!hasChairmanOrViceChairman) {
        this.toasterService.showError('At least one attendee must hold the role of Chairman, Vice Chairman, or Secretary');
      } else if (this.selectedAgendaItems.length === 0) {
        this.toasterService.showError('Please select at least one agenda item');
      }
    }
  }
}
